import {
  filterInstallStatusRows,
  groupInstallStatusByCampaign,
  groupInstallStatusByStore,
  installOutcomeLabel,
  installStatusComparisonText,
  installStatusCycleDay,
  installStatusLabel,
  installStatusNarrative,
  summarizeInstallStatus,
  type InstallStatusRow,
} from '@shared/install-status';

type PreviousCycle = {
  cycleName: string;
  submitted: number;
  leftWithManager: number;
};

export type InstallStatusWorkbookInput = {
  retailerName: string;
  cycleName: string | null;
  cycleStart: string | null;
  cycleEnd: string | null;
  rows: InstallStatusRow[];
  previousCycle: PreviousCycle | null;
  filtered: boolean;
  pulledAt?: Date;
};

const NAVY = 'FF1F4E79';
const BODY = { name: 'Calibri', size: 11 };
const HEADER_FONT = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Calibri', size: 11 };

function formatLongDate(value: string | Date | null): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-IE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Dublin',
  }).format(date);
}

function statusFill(status: string): string | null {
  if (status === 'Installed') return 'FFE2EFDA';
  if (status === 'Not installed') return 'FFFCE4D6';
  if (status === 'Not yet recorded') return 'FFF2F2F2';
  return null;
}

function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

export async function createInstallStatusWorkbook(input: InstallStatusWorkbookInput): Promise<{
  workbook: import('exceljs').Workbook;
  filename: string;
}> {
  const imported = await import('exceljs');
  const ExcelJS = typeof imported.Workbook === 'function' ? imported : imported.default;
  const pulledAt = input.pulledAt ?? new Date();
  const summary = summarizeInstallStatus(input.rows);
  const cycleName = input.cycleName ?? 'This cycle';
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Billposter';
  workbook.created = pulledAt;
  workbook.title = `${input.retailerName} install status`;

  const sheet = workbook.addWorksheet('Summary', { views: [{ showGridLines: false }] });
  sheet.columns = [
    { width: 46 },
    { width: 24 },
    { width: 24 },
    { width: 28 },
    { width: 36 },
  ];

  const title = `${input.retailerName} — ${cycleName} install status`;
  sheet.mergeCells('A1:D1');
  sheet.getCell('A1').value = title;
  sheet.getCell('A1').font = { bold: true, name: 'Calibri', size: 16, color: { argb: 'FF1F4E79' } };

  const startText = formatLongDate(input.cycleStart);
  const endText = formatLongDate(input.cycleEnd);
  const day = input.cycleStart ? installStatusCycleDay(input.cycleStart, pulledAt) : null;
  const range = startText && endText ? ` Cycle runs ${startText}–${endText}.` : '';
  const dayText = day ? ` Day ${day}.` : '';
  const filterText = input.filtered ? ' Campaign and store filters are applied to every sheet.' : '';
  sheet.mergeCells('A2:D2');
  sheet.getCell('A2').value = `Posting tasks only. One row per posting task. Pulled ${formatLongDate(pulledAt)}.${range}${dayText}${filterText}`;
  sheet.getCell('A2').font = { name: 'Calibri', size: 11, italic: true, color: { argb: 'FF595959' } };
  sheet.getRow(2).height = input.filtered ? 32 : 18;

  const section = (row: number, text: string) => {
    sheet.mergeCells(row, 1, row, 4);
    const cell = sheet.getCell(row, 1);
    cell.value = text;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Calibri', size: 13 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    sheet.getRow(row).height = 22;
  };

  section(4, 'Submitted so far');
  ['', 'Tasks', 'Share of submitted', 'Share of planned'].forEach((header, index) => {
    const cell = sheet.getCell(5, index + 1);
    cell.value = header;
    cell.font = { bold: true, name: 'Calibri', size: 11 };
  });

  const summaryRows: Array<[string, number, number | null, number | null]> = [
    ['Installed', summary.installed, summary.installed, summary.installed],
    ['Not installed — left with manager', summary.leftWithManager, summary.leftWithManager, summary.leftWithManager],
    ['Not installed — removed from store', summary.removedFromStore, summary.removedFromStore, summary.removedFromStore],
  ];
  if (summary.submittedWithZeroUnits > 0) {
    summaryRows.push([
      'Not installed — submitted with zero units',
      summary.submittedWithZeroUnits,
      summary.submittedWithZeroUnits,
      summary.submittedWithZeroUnits,
    ]);
  }
  summaryRows.push(
    ['Not yet recorded', summary.notYetRecorded, null, summary.notYetRecorded],
    ['Planned posting tasks', summary.planned, null, null],
    ['Stores', summary.stores, null, null],
  );

  summaryRows.forEach((values, index) => {
    const rowNumber = 6 + index;
    const [label, tasks, submittedPart, plannedPart] = values;
    const submittedShare = submittedPart == null ? null : (summary.submitted === 0 ? null : submittedPart / summary.submitted);
    const plannedShare = plannedPart == null ? null : (summary.planned === 0 ? null : plannedPart / summary.planned);
    [label, tasks, submittedShare, plannedShare].forEach((value, column) => {
      const cell = sheet.getCell(rowNumber, column + 1);
      cell.value = value;
      cell.font = BODY;
      if (column === 1 && typeof value === 'number') cell.numFmt = '#,##0';
      if (column >= 2 && typeof value === 'number') cell.numFmt = '0.0%';
    });
    const fill = label.startsWith('Installed')
      ? 'FFE2EFDA'
      : label.startsWith('Not installed')
        ? 'FFFCE4D6'
        : label.startsWith('Not yet')
          ? 'FFF2F2F2'
          : null;
    if (fill) {
      sheet.getCell(rowNumber, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
    }
  });

  let cursor = 6 + summaryRows.length + 1;
  section(cursor, 'Why not installed');
  cursor += 1;
  ['Reason', 'Left with manager', 'Removed from store', 'Total'].forEach((header, index) => {
    const cell = sheet.getCell(cursor, index + 1);
    cell.value = header;
    cell.font = { bold: true, name: 'Calibri', size: 11 };
  });
  summary.reasons.forEach((reason, index) => {
    const line = [
      reason.reason,
      reason.leftWithManager,
      reason.removedFromStore,
      reason.leftWithManager + reason.removedFromStore,
    ];
    line.forEach((value, column) => {
      const cell = sheet.getCell(cursor + 1 + index, column + 1);
      cell.value = value;
      cell.font = BODY;
      if (column > 0) cell.numFmt = '#,##0';
    });
  });
  cursor += summary.reasons.length + 2;
  sheet.mergeCells(cursor, 1, cursor, 4);
  sheet.getCell(cursor, 1).value = installStatusNarrative(summary);
  sheet.getCell(cursor, 1).font = BODY;
  sheet.getCell(cursor, 1).alignment = { wrapText: true };
  sheet.getRow(cursor).height = 32;

  if (input.previousCycle && !input.filtered) {
    cursor += 2;
    section(cursor, 'Compared with the previous cycle');
    cursor += 1;
    ['Cycle', 'Submitted posting tasks', 'Left with manager', 'Share of submitted'].forEach((header, index) => {
      const cell = sheet.getCell(cursor, index + 1);
      cell.value = header;
      cell.font = { bold: true, name: 'Calibri', size: 11 };
    });
    const currentShare = summary.submitted === 0 ? null : summary.leftWithManager / summary.submitted;
    const previousShare = input.previousCycle.submitted === 0
      ? null
      : input.previousCycle.leftWithManager / input.previousCycle.submitted;
    [
      [input.previousCycle.cycleName, input.previousCycle.submitted, input.previousCycle.leftWithManager, previousShare],
      [`${cycleName}${day ? ` (in progress, day ${day})` : ''}`, summary.submitted, summary.leftWithManager, currentShare],
    ].forEach((values, index) => {
      values.forEach((value, column) => {
        const cell = sheet.getCell(cursor + 1 + index, column + 1);
        cell.value = value;
        cell.font = BODY;
        if (column === 1 || column === 2) cell.numFmt = '#,##0';
        if (column === 3 && typeof value === 'number') cell.numFmt = '0.0%';
      });
    });
    cursor += 4;
    sheet.mergeCells(cursor, 1, cursor, 4);
    sheet.getCell(cursor, 1).value = installStatusComparisonText(
      { cycleName, leftWithManager: summary.leftWithManager, submitted: summary.submitted },
      input.previousCycle,
    );
    sheet.getCell(cursor, 1).font = { name: 'Calibri', size: 11, italic: true, color: { argb: 'FF595959' } };
    sheet.getCell(cursor, 1).alignment = { wrapText: true };
    sheet.getRow(cursor).height = 36;
  }

  const detailColumns = [
    { header: 'Store no.', key: 'storeNo', width: 12 },
    { header: 'Store', key: 'storeName', width: 42 },
    { header: 'Campaign', key: 'campaignName', width: 52 },
    { header: 'Photo request', key: 'photoRequest', width: 16 },
    { header: 'Status', key: 'status', width: 20 },
    { header: 'Outcome', key: 'outcome', width: 28 },
    { header: 'Reason', key: 'reason', width: 42 },
    { header: 'Note', key: 'note', width: 64 },
    { header: 'Operator', key: 'operatorName', width: 24 },
    { header: 'Planned qty', key: 'plannedQty', width: 14 },
    { header: 'Posted units', key: 'postedUnits', width: 14 },
    { header: 'Recorded', key: 'recordedAt', width: 20 },
  ];

  const addDetail = (name: string, data: InstallStatusRow[]) => {
    const detail = workbook.addWorksheet(name);
    detail.columns = detailColumns;
    for (const entry of data) {
      detail.addRow({
        storeNo: entry.storeNo ?? '',
        storeName: entry.storeName,
        campaignName: entry.campaignName,
        photoRequest: yesNo(entry.photoRequest),
        status: installStatusLabel(entry.status),
        outcome: installOutcomeLabel(entry.outcome),
        reason: entry.reason ?? '',
        note: entry.note ?? '',
        operatorName: entry.operatorName ?? '',
        plannedQty: entry.plannedQty,
        postedUnits: entry.postedUnits,
        recordedAt: entry.recordedAt ? new Date(entry.recordedAt) : null,
      });
    }
    const header = detail.getRow(1);
    header.font = HEADER_FONT;
    header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    header.alignment = { vertical: 'middle', wrapText: true };
    header.height = 22;
    detail.views = [{ state: 'frozen', ySplit: 1 }];
    detail.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: Math.max(data.length + 1, 1), column: detailColumns.length },
    };
    detail.eachRow((excelRow, rowNumber) => {
      if (rowNumber === 1) return;
      excelRow.font = BODY;
      excelRow.alignment = { vertical: 'top', wrapText: true };
      const statusCell = excelRow.getCell(5);
      const fill = statusFill(String(statusCell.value ?? ''));
      if (fill) statusCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
    });
    detail.getColumn('plannedQty').numFmt = '#,##0';
    detail.getColumn('postedUnits').numFmt = '#,##0';
    detail.getColumn('recordedAt').numFmt = 'yyyy-mm-dd hh:mm';
  };

  const notInstalled = filterInstallStatusRows(input.rows, {
    campaignId: null,
    storeId: null,
    view: 'not_installed',
    hidePhotoRequests: false,
  });
  addDetail('Not installed', notInstalled);
  addDetail('All store campaigns', input.rows);

  const countColumns = [
    { header: 'Planned', key: 'planned', width: 12 },
    { header: 'Installed', key: 'installed', width: 12 },
    { header: 'Left with manager', key: 'leftWithManager', width: 20 },
    { header: 'Removed from store', key: 'removedFromStore', width: 22 },
    { header: 'Submitted with zero units', key: 'submittedWithZeroUnits', width: 26 },
    { header: 'Not yet recorded', key: 'notYetRecorded', width: 18 },
  ];

  const byCampaign = workbook.addWorksheet('By campaign');
  byCampaign.columns = [
    { header: 'Campaign', key: 'campaignName', width: 56 },
    { header: 'Photo request', key: 'photoRequest', width: 16 },
    ...countColumns,
  ];
  for (const group of groupInstallStatusByCampaign(input.rows)) {
    byCampaign.addRow({ ...group, photoRequest: yesNo(group.photoRequest) });
  }

  const byStore = workbook.addWorksheet('By store');
  byStore.columns = [
    { header: 'Store no.', key: 'storeNo', width: 12 },
    { header: 'Store', key: 'storeName', width: 46 },
    ...countColumns,
    { header: 'Operator on not-installed', key: 'operators', width: 28 },
  ];
  for (const group of groupInstallStatusByStore(input.rows)) byStore.addRow(group);

  for (const grouped of [byCampaign, byStore]) {
    const header = grouped.getRow(1);
    header.font = HEADER_FONT;
    header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    header.alignment = { vertical: 'middle', wrapText: true };
    grouped.views = [{ state: 'frozen', ySplit: 1 }];
    grouped.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: Math.max(grouped.rowCount, 1), column: grouped.columnCount },
    };
    grouped.eachRow((excelRow, rowNumber) => {
      if (rowNumber === 1) return;
      excelRow.font = BODY;
    });
    for (const key of ['planned', 'installed', 'leftWithManager', 'removedFromStore', 'submittedWithZeroUnits', 'notYetRecorded']) {
      grouped.getColumn(key).numFmt = '#,##0';
    }
  }

  const stamp = pulledAt.toISOString().slice(0, 10);
  const slug = (input.cycleName ?? `cycle-${input.rows.length}`).replace(/[^\w]+/g, '-').replace(/^-|-$/g, '');
  return {
    workbook,
    filename: `install-status-${slug || 'cycle'}-${stamp}.xlsx`,
  };
}

export async function downloadInstallStatusWorkbook(input: InstallStatusWorkbookInput): Promise<void> {
  const { workbook, filename } = await createInstallStatusWorkbook(input);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
